/**
 * Site Configuration
 *
 * Central configuration for the application.
 * All values come directly from .env - make sure to configure properly.
 *
 * Architecture:
 * - raw-env.ts: Direct process.env access (Next.js compatible)
 * - env-utils.ts: Parsing utilities
 * - site.ts: Final configuration object (this file)
 */

import { AppImages } from "@/constants";
import { EndpointType } from "@/shared/core/types";
import {
  parseArray,
  parseBoolean,
  parseNumber,
  parseString,
  resolveApiBaseUrl,
  resolveUseLocalBackend,
  type UseLocalBackendSetting,
} from "@/shared/core/utils/env-utils";
import {
  rawEnv,
  isDevelopment,
  isProduction,
  isTest,
} from "@/shared/core/config/raw-env";

// ============================================================================
// COMPUTED VALUES
// ============================================================================

const useLocalBackendSetting = parseString(
  rawEnv.useLocalBackend
) as UseLocalBackendSetting;

const useLocalBackend = resolveUseLocalBackend(
  rawEnv.useLocalBackend,
  isDevelopment
);

const apiBaseUrl = resolveApiBaseUrl(
  rawEnv.apiBaseUrl,
  rawEnv.localBackendUrl,
  rawEnv.productionBackendUrl,
  useLocalBackend
);

const cacheTTL = parseNumber(rawEnv.cacheTTL);

// ============================================================================
// SITE CONFIGURATION
// ============================================================================

/**
 * Site configuration object
 * NOTE: All values come directly from .env - no defaults!
 */
export const siteConfig = {
  // ─────────────────────────────────────────────────────────────────────────
  // App Info
  // ─────────────────────────────────────────────────────────────────────────
  name: parseString(rawEnv.appName),
  description: parseString(rawEnv.appDescription),
  version: parseString(rawEnv.appVersion),
  svgLogo: AppImages.kSvgLogo,
  pngLogo: AppImages.kPngLogo,

  // ─────────────────────────────────────────────────────────────────────────
  // URLs
  // ─────────────────────────────────────────────────────────────────────────
  url: parseString(rawEnv.appUrl),

  // ─────────────────────────────────────────────────────────────────────────
  // API Configuration
  // ─────────────────────────────────────────────────────────────────────────
  apiBaseUrl,
  apiUrls: {
    local: parseString(rawEnv.localBackendUrl),
    production: parseString(rawEnv.productionBackendUrl),
  },
  apiVersion: parseString(rawEnv.apiVersion),
  apiMainPath: parseString(rawEnv.apiMainPath),
  apiTimeout: parseNumber(rawEnv.apiTimeout),

  // ─────────────────────────────────────────────────────────────────────────
  // Backend Selection
  // ─────────────────────────────────────────────────────────────────────────
  useLocalBackendSetting,
  useLocalBackend,

  // ─────────────────────────────────────────────────────────────────────────
  // Security
  // ─────────────────────────────────────────────────────────────────────────
  apiKey: parseString(rawEnv.apiKey),
  secretKey: parseString(rawEnv.encryptionKey),

  // ─────────────────────────────────────────────────────────────────────────
  // reCAPTCHA
  // ─────────────────────────────────────────────────────────────────────────
  recaptchaSiteKey: parseString(rawEnv.recaptchaSiteKey),
  isRecaptchaEnabled: parseString(rawEnv.recaptchaSiteKey).trim().length > 0,

  // ─────────────────────────────────────────────────────────────────────────
  // Contact Information
  // ─────────────────────────────────────────────────────────────────────────
  contact: {
    officeAddress: parseString(rawEnv.contactOfficeAddress),
    workingHours: parseString(rawEnv.contactWorkingHours),
    phone: parseString(rawEnv.contactPhone),
    email: parseString(rawEnv.contactEmail),
    linkedIn: parseString(rawEnv.contactLinkedIn),
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Localization
  // ─────────────────────────────────────────────────────────────────────────
  defaultLocale: parseString(rawEnv.defaultLanguage),
  locales: parseArray(rawEnv.supportedLocales),
  defaultTimeZone: parseString(rawEnv.defaultTimezone),

  // ─────────────────────────────────────────────────────────────────────────
  // Theme
  // ─────────────────────────────────────────────────────────────────────────
  defaultTheme: parseString(rawEnv.defaultTheme) as
    | "light"
    | "dark"
    | "system"
    | "",

  // ─────────────────────────────────────────────────────────────────────────
  // Pagination
  // ─────────────────────────────────────────────────────────────────────────
  defaultPageSize: parseNumber(rawEnv.defaultPageSize),
  maxPageSize: parseNumber(rawEnv.maxPageSize),

  // ─────────────────────────────────────────────────────────────────────────
  // File Upload
  // ─────────────────────────────────────────────────────────────────────────
  maxFileSize: parseNumber(rawEnv.maxFileSize),
  allowedFileTypes: parseArray(rawEnv.allowedFileTypes),

  // ─────────────────────────────────────────────────────────────────────────
  // Cache
  // ─────────────────────────────────────────────────────────────────────────
  cacheTTL,
  enableCache: parseBoolean(rawEnv.enableCache),

  // Query timing (derived from cacheTTL)
  queryStaleTime: cacheTTL * 1000 * 5, // 5x cache TTL
  queryGcTime: cacheTTL * 1000 * 60, // 60x cache TTL

  // ─────────────────────────────────────────────────────────────────────────
  // Feature Flags
  // ─────────────────────────────────────────────────────────────────────────
  enableAnalytics: parseBoolean(rawEnv.enableAnalytics),
  enableErrorReporting: parseBoolean(rawEnv.enableErrorReporting),
  enableLogging: parseBoolean(rawEnv.enableLogging),

  // ─────────────────────────────────────────────────────────────────────────
  // Google Maps
  // ─────────────────────────────────────────────────────────────────────────
  googleMapsApiKey: parseString(rawEnv.googleMapsApiKey),
  googleMapsId: parseString(rawEnv.googleMapsId),

  // ─────────────────────────────────────────────────────────────────────────
  // Endpoint Type
  // ─────────────────────────────────────────────────────────────────────────
  defaultEndpointType:
    rawEnv.defaultEndpoint === "app_api"
      ? EndpointType.APP_API
      : EndpointType.DIRECT_API,

  // ─────────────────────────────────────────────────────────────────────────
  // Environment
  // ─────────────────────────────────────────────────────────────────────────
  isProduction,
  isDevelopment,
  isTest,
} as const;

/**
 * Type for site configuration
 */
export type SiteConfig = typeof siteConfig;
